/**
 * VoiceBot theatrical product stages and residency helpers.
 * VB.js remains the coordinator; this module names stages and capture flags.
 * No external state-machine dependency.
 */

export const PRODUCT_STAGES = Object.freeze({
  WELCOME: 'WELCOME',
  CURTAIN: 'CURTAIN',
  INTRO: 'INTRO',
  SOLAR: 'SOLAR',
  STUDIO: 'STUDIO',
});

export const JOURNEY_ARM_KEY = 'voiceBotJourneyCaptureArm';

/** Hold Solar over the studio while PNGs / dial / avatar finish painting. */
export const STUDIO_ASSEMBLE_MS = 2000;

export function resolveProductStage({
  showVB = false,
  showContent = false,
  vidDemoFinished = false,
  trainerFinished = false,
  cinematicEligible = true,
} = {}) {
  if (!showVB) return PRODUCT_STAGES.WELCOME;
  if (!showContent) return PRODUCT_STAGES.CURTAIN;
  if (!vidDemoFinished && cinematicEligible) return PRODUCT_STAGES.INTRO;
  if (!trainerFinished) return PRODUCT_STAGES.SOLAR;
  return PRODUCT_STAGES.STUDIO;
}

/** Avatar may prewarm during Solar (hidden). Visible only in Studio. */
export function shouldMountAvatar(stage) {
  return stage === PRODUCT_STAGES.SOLAR
    || stage === PRODUCT_STAGES.STUDIO;
}

export function shouldRevealStudio(stage) {
  return stage === PRODUCT_STAGES.STUDIO;
}

/** Intro video trainer is the current stage only — prefetch separately. */
export function shouldMountIntro(stage) {
  return stage === PRODUCT_STAGES.INTRO;
}

export function shouldMountSolar(stage) {
  return stage === PRODUCT_STAGES.SOLAR;
}

/**
 * Studio dressing, YouTube, and dial stay off-stage until Solar is exiting.
 * During the assemble hold, Solar remains the cover while the set mounts behind it.
 */
export function shouldMountStudioSet(stage, assembling = false) {
  if (stage === PRODUCT_STAGES.STUDIO) return true;
  return !!assembling && stage === PRODUCT_STAGES.SOLAR;
}

/** Reporting-only overlap label. Not a product state machine. */
export function resolveReportingSubphase({
  stage,
  curtainDone = true,
  introMounted = false,
  studioAssembling = false,
  avatarMounted = false,
} = {}) {
  if (studioAssembling && stage === PRODUCT_STAGES.SOLAR) return 'STUDIO_ASSEMBLY';
  if (stage === PRODUCT_STAGES.INTRO && !curtainDone) return 'CURTAIN_INTRO_OVERLAP';
  if (stage === PRODUCT_STAGES.SOLAR && avatarMounted && !studioAssembling) {
    return 'SOLAR_AVATAR_PREWARM';
  }
  if (stage === PRODUCT_STAGES.INTRO && introMounted) return PRODUCT_STAGES.INTRO;
  return stage || PRODUCT_STAGES.WELCOME;
}

export function shouldHideAppHexagon(pathname) {
  const path = String(pathname || '');
  return path.startsWith('/voicebot') || path.startsWith('/PodcastTools');
}

export function buildResidencySnapshot({
  stage,
  showVB,
  showContent,
  vidDemoFinished,
  trainerFinished,
  isAssessmentManagerOpen = false,
  boxVisible = false,
  boxTitle = '',
  openAvatarTweakerModal = false,
  showTranscriptTrainer = false,
  captureStarted = false,
  studioAssembling = false,
} = {}) {
  const current = stage || resolveProductStage({
    showVB, showContent, vidDemoFinished, trainerFinished,
  });
  return {
    stage: current,
    firstTimeWelcome: current === PRODUCT_STAGES.WELCOME,
    curtainLoader: current === PRODUCT_STAGES.CURTAIN,
    introTrainer: current === PRODUCT_STAGES.INTRO,
    solarTrainer: current === PRODUCT_STAGES.SOLAR,
    studioSet: shouldMountStudioSet(current, studioAssembling),
    avatarIframe: shouldMountAvatar(current),
    youtube: shouldMountStudioSet(current, studioAssembling),
    assessment: !!isAssessmentManagerOpen,
    talkPanel: !!boxVisible && boxTitle === 'Talk',
    fluencyPanel: !!boxVisible && boxTitle === 'Fluency',
    tuningModal: !!openAvatarTweakerModal,
    transcriptTrainer: !!showTranscriptTrainer,
    displayCapture: !!captureStarted,
  };
}

export function isJourneyArmed() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(JOURNEY_ARM_KEY) === '1';
  } catch {
    return false;
  }
}

export function armFullJourneyCapture() {
  try {
    sessionStorage.setItem(JOURNEY_ARM_KEY, '1');
  } catch {
    /* optional */
  }
}

export function clearJourneyArm() {
  try {
    sessionStorage.removeItem(JOURNEY_ARM_KEY);
  } catch {
    /* optional */
  }
}

export function collectMemoryResidency() {
  if (typeof document === 'undefined') {
    return { available: false };
  }
  const mem = (typeof performance !== 'undefined' && performance.memory) ? performance.memory : null;
  return {
    available: true,
    domNodes: document.getElementsByTagName('*').length,
    canvas: document.getElementsByTagName('canvas').length,
    iframe: document.getElementsByTagName('iframe').length,
    video: document.getElementsByTagName('video').length,
    audio: document.getElementsByTagName('audio').length,
    jsHeapUsed: mem ? mem.usedJSHeapSize : null,
    jsHeapTotal: mem ? mem.totalJSHeapSize : null,
  };
}
